import { ServiceType } from "@eu.mdotm.api/sphere";
import { hasAccess } from "$root/components/AuthorizationGuard";
import { useTypedNavigation } from "$root/components/PlatformRouter/RoutesDef";
import { spawnReferenceCreationDialog } from "$root/functional-areas/portfolio-studio/dialogs/ReferenceCreationDialog";
import { spawnMarketViewCreationDialog } from "$root/functional-areas/portfolio-studio/dialogs/MarketViewCreationDialog";
import { spawnPortfolioCreationDialog } from "$root/functional-areas/portfolio-studio/dialogs/PortfolioCreationDialog";
import { spawnUniverseCreationDialog } from "$root/functional-areas/portfolio-studio/dialogs/UniverseCreationDialog";
import { user$ } from "$root/functional-areas/user";
import { ManualCreationEntitiesEnum } from "$root/pages/ManualCreation/common";
import { Icon } from "@mdotm/shadcn/legacy/components";
import { Button, DropdownMenu, type DropdownTriggerProps } from "@mdotm/shadcn/mdotm/components";
import { asyncStateFrom } from "@mdotm/utils/promise";
import { readAtom } from "@mdotm/shadcn/legacy/reactivity";
import { useTranslation } from "react-i18next";
import { generateIntentId, trackMixPanelEvent } from "$root/third-party-integrations/initMixPanel";

function NewEntityTrigger({
	children,
	state,
	...props
}: DropdownTriggerProps & { children?: React.ReactNode }): React.JSX.Element {
	return (
		<Button
			state={asyncStateFrom(state)}
			progressBar={false}
			size="small"
			palette="primary"
			data-qualifier="PortfolioStudio/New"
			{...props}
		>
			<Icon icon="Outline1" size={16} />
			{children}
		</Button>
	);
}

export function createPortfolio() {
	const intentId = generateIntentId();
	trackMixPanelEvent("portfolio.entityActions.createPortfolioIntent", { intentId });
	spawnPortfolioCreationDialog({ intentId, user: readAtom(user$) });
}

function CreateEntitiesButton(): React.JSX.Element {
	const { push } = useTypedNavigation();
	const { t } = useTranslation();

	return (
		<DropdownMenu
			Trigger={NewEntityTrigger}
			extraTriggerProps={{ children: t("BUTTON.NEW") }}
			actions={[
				{
					label: "Portfolio",
					onClick: createPortfolio,
					"data-qualifier": "PortfolioStudio/New/Portfolio",
				},
				{
					label: "Reference",
					onClick: () => {
						const intentId = generateIntentId();
						trackMixPanelEvent("reference.entityActions.createIntent", { intentId });
						spawnReferenceCreationDialog({ intentId });
					},
					"data-qualifier": "PortfolioStudio/New/Reference",
				},
				{
					label: "Universe",
					onClick: () => {
						const intentId = generateIntentId();
						trackMixPanelEvent("universe.entityActions.createIntent", { intentId });
						if (
							hasAccess(readAtom(user$), {
								requiredServices: [ServiceType.GroupsUniverse],
							})
						) {
							spawnUniverseCreationDialog({ intentId });
						} else {
							push("Portfolios/ManualCreation", {
								entity: ManualCreationEntitiesEnum.Universe,
								intentId,
							});
						}
					},
					"data-qualifier": "PortfolioStudio/New/Universe",
				},
				{
					label: "Market View",
					onClickAsync: async () => {
						const intentId = generateIntentId();
						trackMixPanelEvent("marketView.entityActions.createIntent", { intentId });
						await spawnMarketViewCreationDialog({ intentId });
					},
					"data-qualifier": "PortfolioStudio/New/MarketView",
				},
			]}
		/>
	);
}

export default CreateEntitiesButton;
